"use client";

import { useState } from "react";
import { ChevronDown } from "lucide-react";
import {
  BlockWrapper,
  H2,
  MarkdownRenderer,
  SectionTag,
} from "@/shared/components";
import type { BlockSectionBg, CommonBlockProps } from "./common/types";
import {
  getBodyTextClass,
  getMarkdownTextOverrideClass,
  getSectionBorderClass,
  getSectionHeaderMobilePaddingClass,
  getSectionTaglineSidebarClass,
  sectionBgToWrapper,
} from "./blockSection";
import { displayFontClassName } from "@/lib/fonts";
import { cn } from "@/lib/utils";

interface AccordionItem {
  question: string;
  answer: string;
}

interface AccordionBlockProps extends CommonBlockProps {
  items: AccordionItem[];
}

type AccordionTheme = {
  headline: string;
  bodyText: string;
  question: string;
  tagVariant: "default" | "onTinted";
};

/** Figma block_accordion 2462:1476 — lite, grey (muted), dark. */
const accordionThemeMap: Record<BlockSectionBg, AccordionTheme> = {
  light: {
    headline: "text-foreground",
    bodyText: "text-tag-foreground",
    question: "text-foreground",
    tagVariant: "default",
  },
  default: {
    headline: "text-foreground",
    bodyText: "text-tag-foreground",
    question: "text-foreground",
    tagVariant: "default",
  },
  muted: {
    headline: "text-foreground",
    bodyText: "text-tag-foreground",
    question: "text-foreground",
    tagVariant: "default",
  },
  red: {
    headline: "text-primary-foreground",
    bodyText: "text-header-top-fg",
    question: "text-primary-foreground",
    tagVariant: "onTinted",
  },
  dark: {
    headline: "text-secondary-foreground",
    bodyText: "text-header-top-fg",
    question: "text-secondary-foreground",
    tagVariant: "onTinted",
  },
};

function resolveAccordionTheme(sectionBg: BlockSectionBg): AccordionTheme {
  return accordionThemeMap[sectionBg] ?? accordionThemeMap.light;
}

const descriptionProseClass =
  "font-sans text-base leading-[1.2] [&_a]:text-link [&_li]:mt-0 [&_ol]:list-decimal [&_ol]:pl-6 [&_p+p]:mt-0 [&_ul]:list-disc [&_ul]:pl-6";

const itemHorizontalPaddingClass = "px-4 md:px-6 desktop:px-10";
const questionPaddingClass = "py-6 md:py-6 desktop:py-10";
const answerPaddingClass = "pb-6 md:pb-6 desktop:pb-10";

export function AccordionBlock({
  headline,
  tagline,
  description,
  items,
  sectionBg = "light",
}: AccordionBlockProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  if (!items || items.length === 0) return null;

  const theme = resolveAccordionTheme(sectionBg);
  const borderClass = getSectionBorderClass(sectionBg);
  const bodyTextClass = cn(
    getBodyTextClass(sectionBg),
    theme.bodyText,
    getMarkdownTextOverrideClass(sectionBg)
  );
  const hasSidebar = Boolean(tagline);
  const hasHeader = Boolean(headline || tagline || description);

  return (
    <BlockWrapper bgColor={sectionBgToWrapper(sectionBg)} noPadding>
      <div className="w-full">
        {hasHeader && (
          <div
            className={cn(
              getSectionHeaderMobilePaddingClass(),
              "flex flex-col gap-4 border-b",
              "desktop:flex-row desktop:gap-0 desktop:border-b desktop:p-0",
              borderClass
            )}
          >
            {tagline && (
              <div
                className={cn(
                  getSectionTaglineSidebarClass(),
                  "desktop:w-1/3 desktop:border-r",
                  borderClass
                )}
              >
                <SectionTag
                  label={tagline}
                  variant={theme.tagVariant}
                  className="uppercase"
                />
              </div>
            )}

            <div
              className={cn(
                "flex flex-col gap-4",
                "desktop:flex-1 desktop:px-10 desktop:py-20"
              )}
            >
              {headline && (
                <H2
                  className={cn(
                    "text-heading-2-sm md:text-heading-2",
                    theme.headline
                  )}
                >
                  {headline}
                </H2>
              )}
              {description && (
                <MarkdownRenderer
                  content={description}
                  className={cn(descriptionProseClass, bodyTextClass)}
                />
              )}
            </div>
          </div>
        )}

        <div className="flex flex-col desktop:flex-row">
          {hasSidebar && (
            <div
              className={cn(
                "hidden desktop:block desktop:w-1/3 desktop:border-r",
                borderClass
              )}
            />
          )}

          <div className="flex flex-col desktop:flex-1">
            {items.map((item, index) => {
              const isOpen = openIndex === index;

              return (
                <div
                  key={`${item.question}-${index}`}
                  className={cn("border-b last:border-b-0", borderClass)}
                >
                  <div
                    className={cn(
                      itemHorizontalPaddingClass,
                      questionPaddingClass
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenIndex(isOpen ? null : index)}
                      className={cn(
                        "flex w-full items-center justify-between gap-4 text-left",
                        displayFontClassName,
                        theme.question,
                        "text-heading-3-mobile font-bold leading-[1.2] md:text-heading-3 md:font-semibold md:leading-[1.2]"
                      )}
                      aria-expanded={isOpen}
                    >
                      <span className="min-w-0 flex-1">{item.question}</span>
                      <ChevronDown
                        className={cn(
                          "size-10 shrink-0 transition-transform duration-200",
                          theme.question,
                          isOpen && "rotate-180"
                        )}
                        strokeWidth={1.5}
                        aria-hidden
                      />
                    </button>
                  </div>

                  {isOpen && item.answer && (
                    <div
                      className={cn(
                        itemHorizontalPaddingClass,
                        answerPaddingClass
                      )}
                    >
                      <MarkdownRenderer
                        content={item.answer}
                        className={cn(descriptionProseClass, bodyTextClass)}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </BlockWrapper>
  );
}
